"use client";

import { ReactNode } from "react";
import Image from "next/image";
import { useLanguage } from "../../context/LanguageContext";
import { BulletItem } from "../BulletItem";
import { Header } from "../Header";
import { PageTitle } from "../PageTitle";
import SiteFooter from "../SiteFooter";
import { bodyTextClass, cardHeadingClass } from "../typography";

type CardProps = {
  title: string;
  children: ReactNode;
};

function InfoCard({ title, children }: CardProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className={cardHeadingClass}>{title}</h2>
      <div className={`mt-3 ${bodyTextClass}`}>{children}</div>
    </div>
  );
}

export default function AccessibilityPage(): JSX.Element {
  const { t } = useLanguage();
  const c = t.accessibilityPage;
  const contactEmail = t.common.footer.email;

  return (
    <div className="flex min-h-screen flex-col" dir={t.common.dir} lang={t.common.lang}>
      <Header />
      <main className="relative isolate flex-1 overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -bottom-24 left-[-7rem] h-[320px] w-[520px] opacity-[0.04] dark:opacity-[0.06] rtl:left-auto rtl:right-[-7rem]">
            <Image
              src="/illustrations/signals/signals-wave.svg"
              alt=""
              width={800}
              height={600}
              className="h-full w-full object-contain"
              loading="lazy"
              aria-hidden="true"
            />
          </div>
        </div>
        <section className="scroll-mt-28 py-16">
          <div className="mx-auto max-w-5xl px-4">
            <PageTitle
              label={c.label}
              title={c.title}
              intro={c.intro}
              lastUpdated={c.lastUpdated}
            />

            <div className="grid gap-6">
              <InfoCard title={c.supportedTitle}>
                <ul className="list-none space-y-2">
                  {c.supportedList.map((item) => (
                    <BulletItem key={item} text={item} textClassName={bodyTextClass} />
                  ))}
                </ul>
              </InfoCard>

              <InfoCard title={c.improvementsTitle}>
                <p className="text-start rtl:text-right">{c.improvementsBody}</p>
              </InfoCard>

              <InfoCard title={c.contactTitle}>
                <p className="text-start rtl:text-right">
                  {c.contactBody}{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                  >
                    {contactEmail}
                  </a>
                  .
                </p>
              </InfoCard>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
